import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import api from '../services/api';
import { toast } from 'react-toastify';
import { ArrowLeftIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

export default function BillNew() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [form, setForm] = useState({
        consumerNumber: '',
        consumerName: '',
        dueDate: '',
        feeBeforeDueDate: '',
        feeAfterDueDate: '',
        billingMonth: '',
        description: ''
    });

    // Customer link and search states
    const [consumerQuery, setConsumerQuery] = useState('');
    const [showCustomersDropdown, setShowCustomersDropdown] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customersList, setCustomersList] = useState([]);
    const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
    const dropdownRef = useRef(null);

    // Modal state for adding a customer
    const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
    const [newCustomerForm, setNewCustomerForm] = useState({
        consumerNumber: '',
        name: '',
        email: '',
        mobile: '',
        address: ''
    });

    // Click outside listener for the dropdown
    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowCustomersDropdown(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Debounced customer query search
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsSearchingCustomers(true);
            api.get('/customers', { params: { search: consumerQuery, limit: 10 } })
                .then((res) => {
                    setCustomersList(res.data?.data?.customers || []);
                })
                .catch((err) => {
                    console.error('Error fetching customers:', err);
                })
                .finally(() => {
                    setIsSearchingCustomers(false);
                });
        }, 300);

        return () => clearTimeout(timer);
    }, [consumerQuery]);

    // Handle consumer query typed input
    const handleConsumerQueryChange = (e) => {
        const value = e.target.value;
        setConsumerQuery(value);
        setForm((prev) => ({ ...prev, consumerNumber: value }));

        if (selectedCustomer && value !== selectedCustomer.consumerNumber) {
            setSelectedCustomer(null);
        }
        setShowCustomersDropdown(true);
    };

    // Selecting a customer from the dropdown search
    const handleSelectCustomer = (customer) => {
        setSelectedCustomer(customer);
        setForm((prev) => ({
            ...prev,
            consumerNumber: customer.consumerNumber,
            consumerName: customer.name
        }));
        setConsumerQuery(customer.consumerNumber);
        setShowCustomersDropdown(false);
    };

    // Clearing/Resetting selection
    const handleClearSelection = () => {
        setSelectedCustomer(null);
        setConsumerQuery('');
        setForm((prev) => ({
            ...prev,
            consumerNumber: '',
            consumerName: ''
        }));
    };

    // Customer creation mutation
    const registerCustomer = useMutation(
        (data) => api.post('/customers', data),
        {
            onSuccess: (res) => {
                const newCust = res.data?.data?.customer;
                toast.success('Customer registered successfully');
                handleSelectCustomer(newCust);
                setShowAddCustomerModal(false);
                setNewCustomerForm({
                    consumerNumber: '',
                    name: '',
                    email: '',
                    mobile: '',
                    address: ''
                });
            },
            onError: (err) => {
                toast.error(err.response?.data?.message || 'Failed to register customer');
            }
        }
    );

    const handleAddCustomerSubmit = (e) => {
        e.preventDefault();
        if (!newCustomerForm.consumerNumber || !newCustomerForm.name) {
            toast.error('Consumer Number and Name are required');
            return;
        }
        registerCustomer.mutate(newCustomerForm);
    };

    // Mutation to create a bill
    const createBill = useMutation(
        (data) => api.post('/bills', data),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('bills');
                toast.success('Bill / Challan generated successfully');
                navigate('/bills');
            },
            onError: (err) => {
                toast.error(err.response?.data?.message || 'Failed to create bill');
            }
        }
    );

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.consumerNumber || !form.consumerName || !form.dueDate || !form.feeBeforeDueDate || !form.feeAfterDueDate) {
            toast.error('Please fill in all required fields');
            return;
        }
        createBill.mutate({
            ...form,
            feeBeforeDueDate: parseFloat(form.feeBeforeDueDate),
            feeAfterDueDate: parseFloat(form.feeAfterDueDate)
        });
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4">
                <button onClick={() => navigate('/bills')} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors">
                    <ArrowLeftIcon className="h-5 w-5 text-slate-600 dark:text-slate-300" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Create New Bill</h1>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Generate a custom bill / challan for payment processing</p>
                </div>
            </div>

            <div className="card max-w-2xl">
                <div className="card-header">
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white">Challan Parameters</h3>
                </div>
                <form onSubmit={handleSubmit} className="card-body space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="relative" ref={dropdownRef}>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                Consumer Number <span className="text-red-500">*</span>
                            </label>
                            <div className="relative flex items-center">
                                <input
                                    type="text"
                                    name="consumerNumber"
                                    value={consumerQuery}
                                    onChange={handleConsumerQueryChange}
                                    onFocus={() => setShowCustomersDropdown(true)}
                                    placeholder="e.g. CON-10002"
                                    className="input-field pr-10"
                                    required
                                />
                                {(selectedCustomer || consumerQuery) && (
                                    <button
                                        type="button"
                                        onClick={handleClearSelection}
                                        className="absolute right-3 p-1 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
                                        title="Clear selection"
                                    >
                                        <XMarkIcon className="h-4 w-4" />
                                    </button>
                                )}
                            </div>

                            {/* Dropdown list */}
                            {showCustomersDropdown && (
                                <div className="absolute left-0 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-slate-900 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-50 border border-slate-200 dark:border-slate-800">
                                    {isSearchingCustomers ? (
                                        <div className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400">Searching customers...</div>
                                    ) : customersList.length > 0 ? (
                                        <>
                                            <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 border-b border-slate-100 dark:border-slate-800">
                                                Select Existing Customer
                                            </div>
                                            {customersList.map((cust) => (
                                                <button
                                                    key={cust.id}
                                                    type="button"
                                                    onClick={() => handleSelectCustomer(cust)}
                                                    className="w-full text-left px-4 py-2 hover:bg-indigo-50 dark:hover:bg-slate-800/80 flex justify-between items-center transition-colors border-b border-slate-55 dark:border-slate-800/50"
                                                >
                                                    <div className="flex flex-col">
                                                        <span className="font-mono text-sm font-semibold text-indigo-600 dark:text-indigo-400">{cust.consumerNumber}</span>
                                                        <span className="text-sm text-slate-800 dark:text-slate-200 font-medium">{cust.name}</span>
                                                    </div>
                                                    {cust.mobile && (
                                                        <span className="text-xs text-slate-400 font-mono">{cust.mobile}</span>
                                                    )}
                                                </button>
                                            ))}
                                        </>
                                    ) : (
                                        <div className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                                            No customers found matching "{consumerQuery}"
                                        </div>
                                    )}

                                    <div className="p-2 bg-slate-50 dark:bg-slate-850 border-t border-slate-105 dark:border-slate-800">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setNewCustomerForm((prev) => ({
                                                    ...prev,
                                                    consumerNumber: consumerQuery
                                                }));
                                                setShowAddCustomerModal(true);
                                                setShowCustomersDropdown(false);
                                            }}
                                            className="w-full text-center px-4 py-2 text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-slate-800 rounded transition-colors"
                                        >
                                            + Register New Customer
                                        </button>
                                    </div>
                                </div>
                            )}

                            {selectedCustomer && (
                                <p className="mt-1 text-xs text-green-600 dark:text-green-400 flex items-center gap-1 font-medium">
                                    <CheckCircleIcon className="h-3.5 w-3.5" />
                                    Linked to: {selectedCustomer.name}
                                </p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-707 dark:text-slate-300 mb-1">
                                Consumer Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="consumerName"
                                value={form.consumerName}
                                onChange={handleChange}
                                placeholder="e.g. Jane Smith"
                                className={`input-field ${selectedCustomer ? 'bg-slate-55 dark:bg-slate-800/50 cursor-not-allowed opacity-80' : ''}`}
                                required
                                disabled={!!selectedCustomer}
                            />
                            {selectedCustomer && (
                                <p className="mt-1 text-xs text-slate-400 dark:text-slate-500 font-medium">
                                    Auto-populated from linked customer.
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                Amount Before Due Date (PKR) <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                name="feeBeforeDueDate"
                                value={form.feeBeforeDueDate}
                                onChange={handleChange}
                                placeholder="e.g. 5000"
                                className="input-field"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                Amount After Due Date (PKR) <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                name="feeAfterDueDate"
                                value={form.feeAfterDueDate}
                                onChange={handleChange}
                                placeholder="e.g. 5500"
                                className="input-field"
                                required
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                Due Date <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                name="dueDate"
                                value={form.dueDate}
                                onChange={handleChange}
                                className="input-field"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                Billing Month (Optional)
                            </label>
                            <input
                                type="month"
                                name="billingMonth"
                                value={form.billingMonth}
                                onChange={handleChange}
                                className="input-field"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                            Description / Notes
                        </label>
                        <textarea
                            name="description"
                            value={form.description}
                            onChange={handleChange}
                            placeholder="Internal record details"
                            rows="3"
                            className="input-field"
                        />
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                        <button
                            type="button"
                            onClick={() => navigate('/bills')}
                            className="btn-secondary"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={createBill.isLoading}
                            className="btn-primary"
                        >
                            {createBill.isLoading ? 'Generating...' : 'Create Draft Bill'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Register New Customer Modal */}
            {showAddCustomerModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-xl border border-slate-200 dark:border-slate-800 w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Register New Customer</h3>
                            <button
                                type="button"
                                onClick={() => setShowAddCustomerModal(false)}
                                className="p-1 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors"
                            >
                                <XMarkIcon className="h-5 w-5 text-slate-500 dark:text-slate-400" />
                            </button>
                        </div>
                        <form onSubmit={handleAddCustomerSubmit} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                    Consumer Number <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={newCustomerForm.consumerNumber}
                                    onChange={(e) => setNewCustomerForm({ ...newCustomerForm, consumerNumber: e.target.value })}
                                    placeholder="e.g. CON-10002"
                                    className="input-field"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                    Full Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={newCustomerForm.name}
                                    onChange={(e) => setNewCustomerForm({ ...newCustomerForm, name: e.target.value })}
                                    placeholder="e.g. John Doe"
                                    className="input-field"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    value={newCustomerForm.email}
                                    onChange={(e) => setNewCustomerForm({ ...newCustomerForm, email: e.target.value })}
                                    placeholder="e.g. john@example.com"
                                    className="input-field"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                    Mobile Number
                                </label>
                                <input
                                    type="text"
                                    value={newCustomerForm.mobile}
                                    onChange={(e) => setNewCustomerForm({ ...newCustomerForm, mobile: e.target.value })}
                                    placeholder="e.g. +923001234567"
                                    className="input-field"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                                    Address
                                </label>
                                <textarea
                                    value={newCustomerForm.address}
                                    onChange={(e) => setNewCustomerForm({ ...newCustomerForm, address: e.target.value })}
                                    placeholder="Street address, city"
                                    rows="3"
                                    className="input-field"
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                                <button
                                    type="button"
                                    onClick={() => setShowAddCustomerModal(false)}
                                    className="btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={registerCustomer.isLoading}
                                    className="btn-primary"
                                >
                                    {registerCustomer.isLoading ? 'Registering...' : 'Register Customer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
